import type { ReactNode } from 'react'
import type { RefreshControlProps, ScrollViewProps } from 'react-native'
import React from 'react'
import { NativeModules, RefreshControl, ScrollView } from 'react-native'

import { useWebPullToRefresh } from '../../hooks/useWebPullToRefresh'
import { WebPullToRefreshIndicator } from './WebPullToRefreshIndicator'

type PullToRefreshScrollViewProps = Omit<ScrollViewProps, 'refreshControl'> & {
  refreshing: boolean
  onRefresh: () => Promise<void> | void
  indicatorColor?: string
  refreshControlProps?: Omit<RefreshControlProps, 'refreshing' | 'onRefresh'>
  renderContent?: (props: {
    indicator: ReactNode
    children: ReactNode
  }) => ReactNode
}

export function PullToRefreshScrollView({
  refreshing,
  onRefresh,
  indicatorColor,
  refreshControlProps,
  renderContent,
  onScroll,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  children,
  ...scrollViewProps
}: PullToRefreshScrollViewProps) {
  const hasNativeAnimatedModule =
    NativeModules.NativeAnimatedModule != null ||
    NativeModules.RCTAnimation != null
  const shouldUseNativeRefreshControl = hasNativeAnimatedModule
  const webPullToRefresh = useWebPullToRefresh({
    onRefresh,
    refreshing,
  })
  const indicator = (
    <WebPullToRefreshIndicator
      enabled={webPullToRefresh.isEnabled}
      pullDistance={webPullToRefresh.pullDistance}
      refreshing={refreshing}
      shouldRelease={webPullToRefresh.shouldRelease}
      color={indicatorColor}
    />
  )

  return (
    <ScrollView
      {...scrollViewProps}
      onScroll={(event) => {
        webPullToRefresh.handleScroll(event)
        onScroll?.(event)
      }}
      onTouchStart={(event) => {
        webPullToRefresh.handleTouchStart(event)
        onTouchStart?.(event)
      }}
      onTouchMove={(event) => {
        webPullToRefresh.handleTouchMove(event)
        onTouchMove?.(event)
      }}
      onTouchEnd={(event) => {
        webPullToRefresh.handleTouchEnd(event)
        onTouchEnd?.(event)
      }}
      onPointerDown={(event) => {
        webPullToRefresh.handlePointerDown(event)
        onPointerDown?.(event)
      }}
      onPointerMove={(event) => {
        webPullToRefresh.handlePointerMove(event)
        onPointerMove?.(event)
      }}
      onPointerUp={(event) => {
        webPullToRefresh.handlePointerUp(event)
        onPointerUp?.(event)
      }}
      refreshControl={
        shouldUseNativeRefreshControl ? (
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            {...refreshControlProps}
          />
        ) : undefined
      }
    >
      {renderContent != null ? (
        renderContent({ indicator, children })
      ) : (
        <>
          {indicator}
          {children}
        </>
      )}
    </ScrollView>
  )
}
